import React from 'react';
import { Home, Send, BellRing, User, Plus } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { announceToScreenReader } from '../../lib/accessibility';

// Haptic feedback utility for native feel
const haptic = {
  light: () => navigator.vibrate?.(10),
  medium: () => navigator.vibrate?.(20),
};

// Mobile Nav Item Component - 44px minimum tap target with labels
const MobileNavItem = ({ icon: Icon, label, onClick, isActive, badge, testId, isDark }) => {
  const handleClick = () => {
    haptic.light();
    onClick?.();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };
  
  return (
    <button
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      data-testid={testId}
      aria-label={`${label}${badge > 0 ? `, ${badge} unread` : ''}${isActive ? ', current page' : ''}`}
      aria-current={isActive ? 'page' : undefined}
      role="menuitem"
      tabIndex={0}
      className={`
        flex flex-col items-center justify-center gap-1 
        min-w-[64px] min-h-[56px] px-3 py-2
        rounded-xl transition-all duration-200
        active:scale-95 ${isDark ? 'active:bg-white/10' : 'active:bg-black/10'}
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-offset-2
        ${isActive 
          ? 'text-yellow-400 bg-yellow-400/10' 
          : isDark 
            ? 'text-gray-400 hover:text-white hover:bg-white/5'
            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'
        }
      `}
    >
      <div className="relative">
        <Icon 
          className={`w-6 h-6 ${isActive ? 'fill-yellow-400/20' : ''}`} 
          strokeWidth={isActive ? 2.5 : 2}
          aria-hidden="true"
        />
        {badge > 0 && (
          <span 
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1"
            aria-hidden="true"
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span className="text-[10px] font-medium" aria-hidden="true">{label}</span>
    </button>
  );
};

export const MobileNav = ({ 
  isDark, 
  unreadMessages, 
  unreadNotifications, 
  onUploadClick 
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavigation = (path, pageName) => {
    navigate(path);
    announceToScreenReader(`Navigated to ${pageName}`);
  };

  const handleUpload = () => {
    haptic.medium();
    onUploadClick();
    announceToScreenReader('Upload dialog opened');
  };

  return (
    <>
      {/* Mobile Bottom Navigation Bar - Instagram/YouTube style */}
      <nav 
        className={`md:hidden fixed bottom-0 left-0 right-0 z-50 backdrop-blur-md border-t safe-area-bottom ${
          isDark 
            ? 'bg-black/95 border-white/10'
            : 'bg-white/95 border-gray-200'
        }`}
        data-testid="mobile-bottom-nav"
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="flex items-center justify-around px-2 py-1" role="menubar" aria-label="Navigation menu">
          <MobileNavItem
            icon={Home}
            label="Home"
            onClick={() => handleNavigation('/browse', 'Home')}
            isActive={location.pathname === '/browse'}
            testId="mobile-nav-home"
            isDark={isDark}
          />
          <MobileNavItem
            icon={Send}
            label="Messages"
            onClick={() => handleNavigation('/messages', 'Messages')}
            isActive={location.pathname === '/messages'}
            badge={unreadMessages}
            testId="mobile-nav-messages"
            isDark={isDark}
          />
          
          {/* Center Upload Button - Prominent with haptic */}
          <button
            onClick={handleUpload}
            data-testid="mobile-nav-upload"
            aria-label="Upload new content"
            role="menuitem"
            tabIndex={0}
            className="
              flex items-center justify-center
              w-14 h-14 -mt-4 rounded-2xl
              bg-gradient-to-r from-yellow-400 to-purple-500
              shadow-lg shadow-yellow-500/30
              active:scale-90 transition-all duration-200
              focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black
            "
          >
            <Plus className="w-7 h-7 text-black" strokeWidth={2.5} aria-hidden="true" />
            <span className="sr-only">Upload new content</span>
          </button>
          
          <MobileNavItem
            icon={BellRing}
            label="Alerts"
            onClick={() => announceToScreenReader('Notifications panel coming soon')}
            badge={unreadNotifications}
            testId="mobile-nav-notifications"
            isDark={isDark}
          />
          <MobileNavItem
            icon={User}
            label="Profile"
            onClick={() => handleNavigation('/account-settings', 'Account Settings')}
            isActive={location.pathname === '/account-settings'}
            testId="mobile-nav-profile"
            isDark={isDark}
          />
        </div>
      </nav>

      {/* Spacer for bottom nav on mobile */}
      <div className="md:hidden h-20" aria-hidden="true" />
    </>
  );
};

export default MobileNav;
